import { useContext, useEffect, useState, createContext } from 'react';
import { useLocation } from 'react-router-dom';
import { getLocalStorageItem, setLocalStorageItem } from '../common/utils';

const AdminSettingsContext = createContext();

export const useModuleSettings = (moduleName, defaults = {}) => {
    const context = useContext(AdminSettingsContext);
    if (!context) {
        throw new Error("useModuleSettings must be used within an AdminSettingsProvider");
    }

    const location = useLocation();
    const resolvedModule = moduleName || location.pathname.split('/')[1] || 'global';

    const defaultValues = {
        filter: "all",
        search: "",
        page: 1,
        limit: 10,
        ...defaults
    };

    const moduleState = {
        ...defaultValues,
        ...(context.settings[resolvedModule] || {})
    };

    const setFilter = (filter) => context.updateModuleSettings(resolvedModule, { filter });
    const setSearch = (search) => context.updateModuleSettings(resolvedModule, { search });
    const setPage = (page) => context.updateModuleSettings(resolvedModule, { page });
    const setLimit = (limit) => context.updateModuleSettings(resolvedModule, { limit });
    const setFilters = (filters) => context.updateModuleSettings(resolvedModule, { filters });
    const resetSettings = () => context.resetModuleSettings(resolvedModule);

    return {
        ...moduleState,
        setFilter,
        setSearch,
        setPage,
        setLimit,
        setFilters,
        resetSettings,
        resetAllSettings: context.resetAllSettings
    };
};

const AdminSettingsProvider = ({ children }) => {
    const [settings, setSettings] = useState(() => {
        return getLocalStorageItem("adminModuleSettings", {});
    });

    useEffect(() => {
        setLocalStorageItem("adminModuleSettings", settings);
    }, [settings]);

    const updateModuleSettings = (module, newSettings) => {
        setSettings((prev) => ({
            ...prev,
            [module]: {
                ...(prev[module] || {}),
                ...newSettings
            }
        }));
    };

    const resetModuleSettings = (module) => {
        setSettings((prev) => {
            const next = { ...prev };
            delete next[module];
            return next;
        });
    };

    const resetAllSettings = () => {
        setSettings({});
    };

    return (
        <AdminSettingsContext.Provider value={{ settings, updateModuleSettings, resetModuleSettings, resetAllSettings }}>
            {children}
        </AdminSettingsContext.Provider>
    );
};

export default AdminSettingsProvider;